import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { calcRelativePositionOnWallAxis } from '@geometry'
import { projectRipsOntoTargetWalls } from '@structuralPlanningUtils'
import { AxiosError } from 'axios'
import { filter, find, flatten, reduce, toNumber } from 'lodash-es'
import { v4 as uuid, v4 } from 'uuid'
import { Stack } from '@mui/material'
import { DEFAULT_LINE_LIKE_SETTINGS } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { getModel, getVerticalTransmissionGraph } from '@queries'
import { createRips } from '@mutations'
import CustomSubmitButton from './components/CustomSubmitButton'
import FormFields from './components/FormFields'
import { DefaultValues } from './types'
import { getWallRipConfigMap } from './utils'

interface Props {
  elementGuids: string[]
  hideElementColumn?: boolean
  defaultCreation?: boolean
  /**
   * Pass this if all walls have the same length
   */
  wallLength?: number
}

const MultiEditWallRipsEdges = ({
  elementGuids,
  hideElementColumn = false,
  defaultCreation = false,
  wallLength,
}: Props) => {
  const [formId, setFormId] = useState(v4())

  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()

  const rips = useModelStore(state => state.model.rips)
  const walls = useModelStore(state => state.model.walls)

  const client = useQueryClient()
  const invalidateResults = useResultsInvalidation()

  const { isLoading, mutate } = useMutation({
    mutationFn: async (state: DefaultValues) => {
      if (state.ripCreationMode === 'copyRips' && state.copyRipsOriginWall) {
        const wallsFromStore = useModelStore.getState().model.walls
        const originWall = wallsFromStore.find(w => w.guid === state.copyRipsOriginWall)
        if (!originWall) return

        const additionalRipsInOriginalWall = filter(
          rips,
          rip => rip.wall_guid === state.copyRipsOriginWall && rip.rip_type === 'AdditionalRip',
        )

        const targetWalls = wallsFromStore.filter(w => elementGuids.includes(w.guid))
        const uniqueRipsToCreate = projectRipsOntoTargetWalls(
          additionalRipsInOriginalWall,
          targetWalls,
        )

        if (uniqueRipsToCreate.length > 0) {
          await createRips.request(projectId, uniqueRipsToCreate)
        }
        return
      }

      const newRips: WallRipRequest[] = flatten(
        Object.entries(state.config).map(([guid, config]) => {
          const ripsOnElement = filter(rips, rip => rip.wall_guid === guid)

          const existingRipOnStart = !!find(
            ripsOnElement,
            rip => toNumber(rip.relative_position) === 0,
          )
          const existingRipOnEnd = !!find(
            ripsOnElement,
            rip => toNumber(rip.relative_position) === 1,
          )

          const newRipsThisElement: WallRipRequest[] = []

          if (!existingRipOnStart && config.start) {
            newRipsThisElement.push({
              ...DEFAULT_LINE_LIKE_SETTINGS,
              guid: uuid(),
              wall_guid: guid,
              relative_position: '0',
            })
          }

          if (!existingRipOnEnd && config.end) {
            newRipsThisElement.push({
              ...DEFAULT_LINE_LIKE_SETTINGS,
              guid: uuid(),
              wall_guid: guid,
              relative_position: '1',
            })
          }

          if (state.useRelativePosition) {
            newRipsThisElement.push({
              ...DEFAULT_LINE_LIKE_SETTINGS,
              guid: uuid(),
              wall_guid: guid,
              relative_position: state.relPosition.toString(),
            })
          }

          if (state.usePointPosition && state.ripPoint) {
            const wallsFromStore = useModelStore.getState().model.walls
            const wall = wallsFromStore.find(w => w.guid === guid)
            if (wall) {
              const relativePosition = calcRelativePositionOnWallAxis(wall, state.ripPoint)
              newRipsThisElement.push({
                ...DEFAULT_LINE_LIKE_SETTINGS,
                guid: uuid(),
                wall_guid: guid,
                relative_position: relativePosition.toString(),
              })
            }
          }

          return newRipsThisElement
        }),
      )

      return createRips.request(projectId, newRips)
    },
    onSuccess: async () => {
      await client.invalidateQueries(getModel.getKey(projectId))
      await Promise.all([
        client.invalidateQueries(getModel.getKey(projectId)),
        client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
      ])
      invalidateResults(projectId as string)
      enqueueSnackbar('Rippen erstellt', { variant: 'success' })
      setFormId(v4())
    },
    onError: error => {
      enqueueSnackbar(buildErrorMessage(error as AxiosError, 'Fehler beim Erstellen der Rippen'), {
        variant: 'error',
      })
    },
  })

  const defaultRipPoint = useMemo((): Point => {
    const selectedWalls = walls.filter(w => elementGuids.includes(w.guid))
    const first = selectedWalls[0]
    if (!first?.shape?.points?.length) return new ImmutableVector3(0, 0, 0)
    const pts = first.shape.points
    const p1 = pts[1] ?? pts[0]
    return new ImmutableVector3((pts[0].x + p1.x) / 2, (pts[0].y + p1.y) / 2, (pts[0].z + p1.z) / 2)
  }, [walls, elementGuids])

  const defaultValues: DefaultValues = useMemo(
    () => ({
      config: reduce(
        elementGuids,
        (collector, guid) => {
          return {
            ...collector,
            ...getWallRipConfigMap(guid, rips, defaultCreation),
          }
        },
        {},
      ),
      useRelativePosition: false,
      relPosition: 0,
      usePointPosition: false,
      ripPoint: defaultRipPoint,
      rips,
      ripCreationMode: 'startAndEnd' as const,
      copyRipsOriginWall: null,
    }),
    [elementGuids, rips, defaultCreation, defaultRipPoint],
  )

  return (
    <Form2<DefaultValues>
      key={formId}
      defaultValues={defaultValues}
      onSubmit={state => mutate(state)}
      reinitialize={(currentValues, defaultValues) => {
        const newConfig = Object.entries(defaultValues.config).reduce((acc, [guid, config]) => {
          if (currentValues.config[guid]) {
            return {
              ...acc,
              [guid]: currentValues.config[guid],
            }
          } else {
            return {
              ...acc,
              [guid]: config,
            }
          }
        }, {})

        return {
          ...defaultValues,
          config: newConfig,
          ripPoint: currentValues.ripPoint ?? defaultValues.ripPoint,
          ripCreationMode: currentValues.ripCreationMode ?? defaultValues.ripCreationMode,
          copyRipsOriginWall: currentValues.copyRipsOriginWall ?? defaultValues.copyRipsOriginWall,
        }
      }}
    >
      <Stack direction="column" spacing={1}>
        <FormFields hideElementColumn={hideElementColumn} length={wallLength} />

        <CustomSubmitButton loading={isLoading} />
      </Stack>
    </Form2>
  )
}

export default MultiEditWallRipsEdges
